import { isPlatformBrowser } from '@angular/common';
import { computed, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';

import type { GetCurrentUserResponse } from 'client/types.gen';

import { PtAuthApiService } from './auth.api.service';
import type { PtAuthSessionStatus, PtSignInCredentials } from './auth.types';

/**
 * Signal-based auth session store backed by cookie-authenticated API calls.
 */
@Injectable({ providedIn: 'root' })
export class PtAuthSessionService {
  private readonly authApi = inject(PtAuthApiService);
  private readonly platformId = inject(PLATFORM_ID);

  private readonly statusState = signal<PtAuthSessionStatus>('unknown');
  private readonly userState = signal<GetCurrentUserResponse | null>(null);

  private initializationRequest: Promise<boolean> | null = null;

  /** Current auth-session status. */
  readonly status = computed(() => this.statusState());

  /** Current user profile for the active session, if authenticated. */
  readonly user = computed(() => this.userState());

  /** Convenience boolean for authenticated session checks. */
  readonly isAuthenticated = computed(() => this.statusState() === 'authenticated');

  readonly isAdmin = computed(() => this.user()?.isSystemUser ?? false);

  /**
   * Ensures session state is resolved.
   *
   * Uses a cached in-flight request to prevent duplicate bootstrap calls.
   */
  initializeSession(force = false): Promise<boolean> {
    if (!isPlatformBrowser(this.platformId)) {
      this.clearSession();
      return Promise.resolve(false);
    }

    if (!force && this.statusState() !== 'unknown') {
      return Promise.resolve(this.isAuthenticated());
    }

    if (!force && this.initializationRequest) {
      return this.initializationRequest;
    }

    const request = this.refreshCurrentUser();
    this.initializationRequest = request.finally(() => {
      this.initializationRequest = null;
    });

    return this.initializationRequest;
  }

  /** Signs in and refreshes the active user profile. */
  async signIn(credentials: PtSignInCredentials): Promise<void> {
    await this.authApi.signIn(credentials);
    await this.refreshCurrentUser();
  }

  /** Signs out and clears the local auth session state. */
  async signOut(): Promise<void> {
    if (isPlatformBrowser(this.platformId)) {
      try {
        await this.authApi.signOut();
      } catch {
        // We still clear local state even if server-side sign-out fails.
      }
    }

    this.clearSession();
  }

  /** Clears local session state after an unauthorized API response. */
  handleUnauthorized(): void {
    this.clearSession();
  }

  /** Loads current user; when it fails, the session is treated as anonymous. */
  private async refreshCurrentUser(): Promise<boolean> {
    try {
      const user = await this.authApi.getCurrentUser();
      this.userState.set(user);
      this.statusState.set('authenticated');
      return true;
    } catch {
      this.clearSession();
      return false;
    }
  }

  /** Resets session state to an unauthenticated baseline. */
  private clearSession(): void {
    this.userState.set(null);
    this.statusState.set('unauthenticated');
  }
}
